import Layout from "@/components/elements/Layout";
import PageLoading from "@/components/PageLoading";
import SelectCountry from "@/components/SelectCountry";
import SelectIndustry from "@/components/SelectIndustry";
import SelectRole from "@/components/SelectRole";
import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useAuth from "@/hooks/auth";
import { useUserProfileForm } from "@/hooks/user";
import { Notebook, User } from "@phosphor-icons/react";
import { useRouter } from "next/router";
import toast from "react-hot-toast";

export default function ProfileRegister() {
  const router = useRouter();
  const { user, csrf } = useAuth({ middleware: "auth" });
  const {
    companyName,
    jobTitle,
    role,
    industry,
    country,
    setCompanyName,
    setJobTitle,
    setRole,
    setIndustry,
    setCountry,
    isUpdating,
    updateProfile,
  } = useUserProfileForm({ onSuccessCallback: onSuccessCallback });

  function onSuccessCallback(response) {
    toast.success("Profile updated!");
    router.push(
      `/register/user-preferences${
        router.query.redirect_to
          ? `?redirect_to=${router.query.redirect_to}`
          : ""
      }`
    );
  }

  const save = function (evt) {
    evt.preventDefault();
    updateProfile();
  };

  if (!user) {
    return <PageLoading />;
  }

  return (
    <div className='min-h-screen pb-8 bg-gradient-to-b from-white/10 to-background lg:pb-16'>
      <div className='max-w-2xl px-4 pt-6 mx-auto'>
        <div className='inline-flex border divide-x rounded-lg divide-white/10 bg-white/10 border-white/10'>
          <div className='flex items-center p-3 text-left gap-x-2'>
            <span className='inline-flex items-center justify-center w-8 h-8 bg-white rounded-sm text-background'>
              <User className='text-lg' weight='bold' />
            </span>
            <div className='flex flex-col'>
              <span className='text-[10px] font-medium leading-none text-transparent bg-clip-text bg-gradient-to-tr from-yellow-1 to-red-1'>
                Step 1/3
              </span>
              <p className='mt-1.5 text-sm font-medium leading-none text-white'>
                Profile Information
              </p>
            </div>
          </div>
          <div className='flex items-center p-3 text-left gap-x-2'>
            <span className='inline-flex items-center justify-center w-8 h-8 text-white rounded-sm'>
              <User className='text-lg' weight='bold' />
            </span>
            {/* <div className='flex flex-col'>
              <span className='text-[10px] font-medium leading-none text-transparent bg-clip-text bg-gradient-to-tr from-yellow-1 to-red-1'>
                Step 2/3
              </span>
              <p className='mt-1.5 text-sm font-medium leading-none text-white'>
                Topic Preferences
              </p>
            </div> */}
          </div>
          <div className='flex items-center p-3 text-left gap-x-2'>
            <span className='inline-flex items-center justify-center w-8 h-8 text-white rounded-sm'>
              <Notebook className='text-lg' weight='bold' />
            </span>
            {/* <div className='flex flex-col'>
              <span className='text-[10px] font-medium leading-none text-transparent bg-clip-text bg-gradient-to-tr from-yellow-1 to-red-1'>
                Step 3/3
              </span>
              <p className='mt-1.5 text-sm font-medium leading-none text-white'>
                Content Preferences
              </p>
            </div> */}
          </div>
        </div>
        <div className='mt-12 lg:mt-20'>
          <p className='mb-2 text-4xl font-bold leading-tight text-white font-display md:text-6xl'>
            Welcome {user.first_name}!
          </p>
          <p className='mt-1 text-sm text-white lg:text-base lg:mt-3 text-opacity-80'>
            {`It's nice to have you joining us! How about tell us more about yourself`}
          </p>
          <div className='pt-5 lg:pt-8'>
            <form
              onSubmit={save}
              className='grid max-w-screen-md grid-cols-2 gap-x-4 gap-y-5'
            >
              <div className='col-span-2 space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='company'>
                  Your Company <span className='text-red-500'>*</span>
                </Label>
                <Input
                  type='text'
                  id='company'
                  required
                  value={companyName}
                  onInput={(evt) => setCompanyName(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='job-title'>
                  Job Title <span className='text-red-500'>*</span>
                </Label>
                <Input
                  type='text'
                  id='job-title'
                  required
                  value={jobTitle}
                  onInput={(evt) => setJobTitle(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='space-y-1.5 text-left'>
                <Label className='text-xs'>
                  Select Role <span className='text-red-500'>*</span>
                </Label>
                <SelectRole
                  isModal={false}
                  value={role}
                  onChange={(value) => setRole(value)}
                />
              </div>
              <div className='space-y-1.5 text-left'>
                <Label className='text-xs'>
                  Industry <span className='text-red-500'>*</span>
                </Label>
                <SelectIndustry
                  isModal={false}
                  value={industry}
                  onChange={(value) => setIndustry(value)}
                />
              </div>
              <div className='space-y-1.5 text-left'>
                <Label className='text-xs'>
                  Country of Residence <span className='text-red-500'>*</span>
                </Label>
                <SelectCountry
                  isModal={false}
                  value={country}
                  onChange={(value) => setCountry(value)}
                />
              </div>
              <div className='col-span-2'>
                <Button
                  type='submit'
                  size='lg'
                  className='text-base text-white bg-gradient-to-tr from-yellow-1 to-red-1'
                >
                  {isUpdating && <Spinner />}
                  {isUpdating ? "Updating..." : "Next Step"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
